// SharingGroupsList.js - Manage sharing groups
import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from './Toast';
import { useNavigate } from 'react-router-dom';
import {
  getUserSharingGroups,
  deleteSharingGroup,
  updateSharingGroup,
  createSharingGroup,
  getSharingGroupUrl,
  getSharingGroup,
  generateShareGroupToken
} from '../services/sharingGroups';
import { FaTrash, FaPencilAlt, FaCopy, FaTimes, FaCheck, FaEdit, FaPlus } from 'react-icons/fa';
import '../App.css';

const SharingGroupsList = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [sharingGroups, setSharingGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editPermission, setEditPermission] = useState('read');
  const [editIsPublic, setEditIsPublic] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupPermission, setNewGroupPermission] = useState('read');
  const [newGroupIsPublic, setNewGroupIsPublic] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    if (user && !user.isAnonymous) {
      loadSharingGroups();
    } else {
      setLoading(false);
    }
  }, [user]);

  const loadSharingGroups = async () => {
    try {
      setLoading(true);
      const groups = await getUserSharingGroups(user.uid);
      setSharingGroups(groups);
    } catch (error) {
      console.error('Error loading sharing groups:', error);
      showToast('Failed to load sharing groups.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (groupId, groupName) => {
    if (!window.confirm(`Are you sure you want to delete "${groupName}"? This will remove all shared access.`)) {
      return;
    }

    try {
      await deleteSharingGroup(groupId);
      showToast('Sharing group deleted successfully.');
      loadSharingGroups();
    } catch (error) {
      console.error('Error deleting sharing group:', error);
      showToast('Failed to delete sharing group.');
    }
  };

  const handleStartEdit = (group) => {
    setEditingGroupId(group.id);
    setEditName(group.name);
    setEditPermission(group.permission);
    setEditIsPublic(group.isPublic);
  };

  const handleCancelEdit = () => {
    setEditingGroupId(null);
    setEditName('');
    setEditPermission('read');
    setEditIsPublic(false);
  };

  const handleSaveEdit = async (groupId) => {
    if (!editName.trim()) {
      showToast('Please enter a group name.');
      return;
    }

    try {
      // Get current group to check if we need to generate a token
      const currentGroup = await getSharingGroup(groupId);
      const updates = {
        name: editName.trim(),
        permission: editPermission,
        isPublic: editIsPublic
      };

      // If enabling public URL and no token exists, generate one
      if (editIsPublic && !currentGroup.token) {
        updates.token = generateShareGroupToken();
      }

      // If disabling public URL, optionally clear the token (or keep it for future use)
      // We'll keep the token in case they want to re-enable it later

      await updateSharingGroup(groupId, updates);
      showToast('Sharing group updated successfully.');
      handleCancelEdit();
      loadSharingGroups();
    } catch (error) {
      console.error('Error updating sharing group:', error);
      showToast('Failed to update sharing group.');
    }
  };

  const handleCopyUrl = async (group) => {
    // If group is public but has no token, generate one
    if (group.isPublic && !group.token) {
      try {
        const token = generateShareGroupToken();
        await updateSharingGroup(group.id, { token });
        // Reload to get updated group
        await loadSharingGroups();
        // Get updated group and copy URL
        const updatedGroups = await getUserSharingGroups(user.uid);
        const updatedGroup = updatedGroups.find(g => g.id === group.id);
        if (updatedGroup) {
          const url = getSharingGroupUrl(updatedGroup);
          if (url) {
            navigator.clipboard.writeText(url).then(() => {
              showToast('URL generated and copied to clipboard!');
            }).catch(err => {
              console.error('Failed to copy:', err);
              showToast('Failed to copy URL.');
            });
            return;
          }
        }
      } catch (error) {
        console.error('Error generating token:', error);
        showToast('Failed to generate public URL. Please try again.');
        return;
      }
    }

    const url = getSharingGroupUrl(group);
    if (url) {
      navigator.clipboard.writeText(url).then(() => {
        showToast('URL copied to clipboard!');
      }).catch(err => {
        console.error('Failed to copy:', err);
        showToast('Failed to copy URL.');
      });
    } else {
      showToast('This group does not have a public URL. Please enable "Public URL" in edit mode.');
    }
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) {
      showToast('Please enter a group name.');
      return;
    }

    setIsCreating(true);
    try {
      await createSharingGroup({
        ownerId: user.uid,
        name: newGroupName.trim(),
        permission: newGroupPermission,
        isPublic: newGroupIsPublic,
        allowedUsers: []
      });
      showToast('Sharing group created successfully!');
      setShowCreateModal(false);
      setNewGroupName('');
      setNewGroupPermission('read');
      setNewGroupIsPublic(false);
      loadSharingGroups();
    } catch (error) {
      console.error('Error creating sharing group:', error);
      showToast('Failed to create sharing group. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  const getResourceCount = (group) => {
    const runs = group.resources?.runs?.length || 0;
    const racers = group.resources?.racers?.length || 0;
    const groups = group.resources?.groups?.length || 0;
    return runs + racers + groups;
  };

  if (!user || user.isAnonymous) {
    return (
      <div style={{ width: '100%', padding: '40px 20px', textAlign: 'center' }}>
        <p>Please sign in to manage sharing groups.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ width: '100%', padding: '40px 20px', textAlign: 'center' }}>
        <p>Loading sharing groups...</p>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', padding: '30px 20px', boxSizing: 'border-box', maxWidth: '100%' }}>
      <div className="homepage-other-tools">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h1 style={{ margin: 0 }}>Sharing Groups</h1>
          <button
            onClick={() => setShowCreateModal(true)}
            style={{
              padding: '10px 20px',
              backgroundColor: '#13A1A8',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 'medium',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <FaPlus /> Create New Group
          </button>
        </div>

        {sharingGroups.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <p style={{ color: '#666', marginBottom: '20px' }}>
              You don't have any sharing groups yet.
            </p>
            <p style={{ color: '#666' }}>
              Create one by selecting resources and clicking "Share" in Runs, Racers, or Groups pages.
            </p>
          </div>
        ) : (
          <div style={{
            border: '1px solid #ddd',
            borderRadius: '4px',
            backgroundColor: '#fff',
            overflow: 'hidden'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600' }}>Name</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600' }}>Permission</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600' }}>Access</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600' }}>Resources</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sharingGroups.map((group, index) => (
                  <tr
                    key={group.id}
                    style={{
                      borderBottom: index < sharingGroups.length - 1 ? '1px solid #eee' : 'none'
                    }}
                  >
                    <td style={{ padding: '12px 16px' }}>
                      {editingGroupId === group.id ? (
                        <input
                          type="text"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '6px 8px',
                            border: '1px solid #13A1A8',
                            borderRadius: '3px',
                            fontSize: '14px'
                          }}
                          autoFocus
                        />
                      ) : (
                        <strong>{group.name}</strong>
                      )}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      {editingGroupId === group.id ? (
                        <select
                          value={editPermission}
                          onChange={(e) => setEditPermission(e.target.value)}
                          style={{
                            padding: '6px 8px',
                            border: '1px solid #13A1A8',
                            borderRadius: '3px',
                            fontSize: '14px'
                          }}
                        >
                          <option value="read">Read</option>
                          <option value="write">Write</option>
                        </select>
                      ) : (
                        <span style={{ textTransform: 'capitalize' }}>{group.permission}</span>
                      )}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      {editingGroupId === group.id ? (
                        <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={editIsPublic}
                            onChange={(e) => setEditIsPublic(e.target.checked)}
                            style={{ marginRight: '5px' }}
                          />
                          <span style={{ fontSize: '0.9em' }}>Public URL</span>
                        </label>
                      ) : (
                        <span style={{ fontSize: '0.9em', color: group.isPublic ? '#28a745' : '#666' }}>
                          {group.isPublic ? 'Public' : 'Private'}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      <span style={{ fontSize: '0.9em', color: '#666' }}>
                        {getResourceCount(group)} resource{getResourceCount(group) !== 1 ? 's' : ''}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      {editingGroupId === group.id ? (
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                          <button
                            onClick={() => handleSaveEdit(group.id)}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: '#13A1A8',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '5px'
                            }}
                          >
                            <FaCheck /> Save
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: '#999',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '5px'
                            }}
                          >
                            <FaTimes /> Cancel
                          </button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                          {group.isPublic && group.token && (
                            <button
                              onClick={() => handleCopyUrl(group)}
                              style={{
                                padding: '6px 12px',
                                backgroundColor: '#13A1A8',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '5px',
                                fontSize: '0.85em'
                              }}
                              title="Copy public URL"
                            >
                              <FaCopy /> URL
                            </button>
                          )}
                          {group.isPublic && !group.token && (
                            <span style={{ fontSize: '0.85em', color: '#999', padding: '6px 12px' }} title="Public URL will be generated when you save">
                              No URL yet
                            </span>
                          )}
                          <button
                            onClick={() => handleStartEdit(group)}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: '#17a2b8',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '5px',
                              fontSize: '0.85em'
                            }}
                            title="Edit group"
                          >
                            <FaEdit />
                          </button>
                          <button
                            onClick={() => handleDelete(group.id, group.name)}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: '#dc3545',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '5px',
                              fontSize: '0.85em'
                            }}
                            title="Delete group"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Create New Group Modal */}
        {showCreateModal && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10000,
              padding: '20px'
            }}
            onClick={() => setShowCreateModal(false)}
          >
            <div
              style={{
                background: '#fff',
                borderRadius: '8px',
                width: '100%',
                maxWidth: '500px',
                padding: '20px',
                position: 'relative',
                boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 style={{ margin: 0 }}>Create New Sharing Group</h2>
                <button
                  onClick={() => setShowCreateModal(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    color: '#666',
                    padding: '5px',
                    lineHeight: 1
                  }}
                >
                  <FaTimes />
                </button>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                <div>
                  <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
                    Group Name:
                  </label>
                  <input
                    type="text"
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    placeholder="e.g., Shared with Coach John"
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '10px' }}>
                    Permission:
                  </label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        value="read"
                        checked={newGroupPermission === 'read'}
                        onChange={(e) => setNewGroupPermission(e.target.value)}
                        style={{ marginRight: '8px' }}
                      />
                      <div>
                        <div style={{ fontWeight: 'medium' }}>Read</div>
                        <div style={{ fontSize: '0.85em', color: '#666' }}>
                          Recipients can view content but cannot edit
                        </div>
                      </div>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        value="write"
                        checked={newGroupPermission === 'write'}
                        onChange={(e) => setNewGroupPermission(e.target.value)}
                        style={{ marginRight: '8px' }}
                      />
                      <div>
                        <div style={{ fontWeight: 'medium' }}>Write</div>
                        <div style={{ fontSize: '0.85em', color: '#666' }}>
                          Recipients can view and edit content
                        </div>
                      </div>
                    </label>
                  </div>
                </div>

                <div>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={newGroupIsPublic}
                      onChange={(e) => setNewGroupIsPublic(e.target.checked)}
                      style={{ marginRight: '8px' }}
                    />
                    <div>
                      <div style={{ fontWeight: 'medium' }}>Public URL</div>
                      <div style={{ fontSize: '0.85em', color: '#666' }}>
                        Generate a public URL that anyone can access
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setNewGroupName('');
                    setNewGroupPermission('read');
                    setNewGroupIsPublic(false);
                  }}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#ccc',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: 'medium'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateGroup}
                  disabled={isCreating || !newGroupName.trim()}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: isCreating || !newGroupName.trim() ? '#ccc' : '#13A1A8',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: isCreating || !newGroupName.trim() ? 'not-allowed' : 'pointer',
                    fontWeight: 'medium'
                  }}
                >
                  {isCreating ? 'Creating...' : 'Create Group'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SharingGroupsList;
